import { Weapon } from '../../Weapon.js';
import { FUSED_WEAPONS } from '../../../../../config/constants.js';

/**
 * Nova Ward (Harmonics fusion: Nova Pulse + Orbit Ward) — a standing ring
 * of orbiting shards, each of which blooms a micro-pulse on a shared beat.
 * Orbit Ward's persistent-shard machinery (spawn once, re-sync every frame)
 * with Nova Pulse's ring-styled AoE riding on top. At max fusion level the
 * micro-pulses also leave brief lingering zones, reusing Nova Pulse's
 * capstone shape (stationary many-hit projectile with a hit cooldown).
 */
export class NovaWard extends Weapon {
  constructor() {
    super(FUSED_WEAPONS.novaWard);
    this._shards = [];
    this._pulseTimer = this.config.pulseInterval;
  }

  dispose() {
    for (const shard of this._shards) shard.active = false;
    this._shards = [];
  }

  /** Like OrbitWard, not timer-fired — shards persist, so this replaces fire() entirely. */
  update(dt, origin, _target, pool) {
    this.fireFlash = Math.max(0, this.fireFlash - dt);
    this._shards = this._shards.filter((s) => s.active);

    while (this._shards.length < this.config.shardCount) {
      this._shards.push(pool.fire({
        x: origin.x,
        y: origin.y,
        radius: this.config.projectileRadius,
        damage: this.config.shardDamage + this.level * this.config.shardDamagePerLevel,
        hits: 1e9,
        hitCooldownDuration: this.config.hitCooldownDuration,
        lifetime: 1e9,
        color: this.config.color,
        knocksBack: true,
        knockbackForce: 12,
        orbitCenter: origin,
        orbitRadius: this.config.orbitRadius,
        orbitAngle: 0,
        orbitAngularSpeed: this.config.angularSpeed,
      }));
    }

    // Same every-frame re-sync OrbitWard does, so the ring never desyncs.
    if (this._shards.length > 0) {
      const anchorAngle = this._shards[0].orbitAngle;
      this._shards.forEach((shard, i) => {
        shard.orbitRadius = this.config.orbitRadius;
        shard.orbitAngle = anchorAngle + (i / this._shards.length) * Math.PI * 2;
        shard.damage = this.config.shardDamage + this.level * this.config.shardDamagePerLevel;
      });
    }

    this._pulseTimer -= dt;
    if (this._pulseTimer <= 0) {
      this._pulseTimer = this.config.pulseInterval;
      const pulseDmg = this.config.pulseDamage + this.level * this.config.pulseDamagePerLevel;
      const pulseRadius = this.config.pulseRadius + this.level * 2;
      for (const shard of this._shards) {
        pool.fire({
          x: shard.x,
          y: shard.y,
          radius: pulseRadius,
          damage: pulseDmg,
          hits: 1,
          lifetime: 0.15,
          color: this.config.color,
          style: 'ring',
        });
        if (this.level >= this.config.maxLevel) {
          pool.fire({
            x: shard.x,
            y: shard.y,
            radius: pulseRadius * this.config.lingerRadiusScale,
            // Not rounded: a lingering zone re-ticks (hits/hitCooldownDuration
            // below) so it is worth several times its per-tick damage, and
            // rounding a small per-tick number is a large error once it does.
            damage: pulseDmg * this.config.lingerDamageScale,
            hits: 1e9,
            hitCooldownDuration: 0.25,
            lifetime: 0.9,
            color: this.config.color,
            style: 'ring',
          });
        }
      }
      this.fireFlash = 0.12;
    }
  }
}
